import { useState, useEffect, useRef, MouseEvent } from 'react';
import RequireCss from '@helpers/RequireCss';
import { ButtonModel, ModalPopUpModel, SelectModalModel } from '@_types/types';
import Button from '@atoms/buttons/Button';
import _ from 'lodash';

interface Props extends ModalPopUpModel {}

let confirmCallback: (() => any) | undefined,
  closeCallback: (() => any) | undefined,
  openCallback: (() => any) | undefined,
  selfControl: boolean | undefined = false,
  isOutsideClickCallback: boolean | undefined = false;

const Modal = (model: Props[]) => {
  const confirmationModal: ModalPopUpModel = {
    id: '',
    title: 'Are you sure?',
    content: '<p>Logging out.</p>',
    confirmButton: {
      styleModifier: ['primary'],
      type: 'button',
      ariaLabel: 'confirm logout',
      text: 'Confirm logout',
    },
    cancelButton: {
      styleModifier: ['secondary-colour'],
      type: 'button',
      ariaLabel: 'cancel',
      text: 'Cancel',
    },
  };
  const [selectedPopUp, setSelectedPopUp] = useState(confirmationModal);
  let { confirmButton, cancelButton, content, title, id } = selectedPopUp;
  const ref = useRef<HTMLDivElement>(null);

  const dismissButton: ButtonModel = {
    type: 'button',
    icon: {
      iconPath: '#saig-close',
      viewBoxHeight: 18,
      viewBoxWidth: 18,
    },
    ariaLabel: 'close modal',
  };

  const closeModal = () => {
    if (ref?.current) {
      ref.current.classList.add('display-none');
      document?.querySelector('body')?.classList.remove('modal-overlay-on');
      window.currentModalId = undefined;
    }
  };
  const openModal = () => {
    if (ref?.current) {
      ref.current.classList.remove('display-none');
      document?.querySelector('body')?.classList.add('modal-overlay-on');
    }
  };

  useEffect(() => {
    function handleSelectModal(event: CustomEvent<SelectModalModel>) {
      const popUpId = event?.detail.popUpId;
      const modalData = event?.detail.modalData;
      let selectedPopupModel;

      confirmCallback = event?.detail?.confirmCallback;
      closeCallback = event?.detail?.closeCallback;
      openCallback = event?.detail?.openCallback;
      selfControl = event?.detail?.selfControl;
      isOutsideClickCallback = event?.detail?.isOutsideClickCallback;
      selectedPopupModel = _.find(model, { id: popUpId });

      if (!selectedPopupModel) {
        return;
      }

      if (modalData) {
        selectedPopupModel = _.merge(selectedPopupModel, modalData);
      }

      window.currentModalId = popUpId ?? 'custom modal';

      selectedPopupModel!.confirmButton = selectedPopupModel!.confirmButton
        ? _.merge({}, confirmationModal?.confirmButton, selectedPopupModel?.confirmButton)
        : undefined;
      selectedPopupModel!.cancelButton = selectedPopupModel!.cancelButton
        ? _.merge({}, confirmationModal?.cancelButton, selectedPopupModel?.cancelButton)
        : undefined;

      selectedPopupModel && setSelectedPopUp(selectedPopupModel);
      selfControl && openModal();
      openCallback && openCallback();
    }

    if (typeof window !== 'undefined') {
      window.addEventListener('selectModal', handleSelectModal);
      window.addEventListener('closeModal', closeModal);
      window.addEventListener('click', documentClickHandler);

      return () => {
        window.removeEventListener('click', documentClickHandler);
        window.removeEventListener('closeModal', closeModal);
        window.removeEventListener('selectModal', handleSelectModal);
      };
    }
  }, []);

  const handleCloseClick = (e: MouseEvent) => {
    typeof closeCallback === 'function' && closeCallback();

    selfControl && closeModal();
  };

  const documentClickHandler = (e: Event) => {
    const modalPopupMain = document?.querySelector<HTMLDivElement>('.saig-o-modal__main');
    const target = e.target as HTMLElement;
    if (target.classList.contains('saig-o-modal')) {
      if (!modalPopupMain?.contains(target)) {
        isOutsideClickCallback && closeCallback && closeCallback();
        closeModal();
      }
    } else if (target.classList.contains('open-login')) {
      window.dispatchEvent(new CustomEvent('openLogin'));
      selfControl && closeModal();
    }
  };

  const handleConfirmClick = (e: MouseEvent) => {
    typeof confirmCallback === 'function' && confirmCallback();

    selfControl && closeModal();
  };

  return (
    <div id={id} ref={ref} className="saig-o-modal display-none">
      <section className="saig-o-modal__main">
        <div className="saig-o-modal__header">
          {title && <div className="saig-o-modal__title">{title}</div>}

          <div className="saig-o-modal__close">{dismissButton && <Button onClick={handleCloseClick} {...dismissButton} />}</div>
        </div>

        {content && <div className="saig-o-modal__content" dangerouslySetInnerHTML={{ __html: content }} />}

        {(cancelButton || confirmButton) && (
          <div className="saig-o-modal__footer">
            {cancelButton && <Button onClick={handleCloseClick} type="button" {...cancelButton} />}

            {confirmButton && <Button onClick={handleConfirmClick} type="button" {...confirmButton} />}
          </div>
        )}
      </section>

      <RequireCss path="b-modal-popup" />
    </div>
  );
};

export { Modal };
export default Modal;
